import { useState, useMemo } from "react";
import type { Species } from "@/mocks/configuracoes";
import SettingsStatusBadge from "./SettingsStatusBadge";

interface SpeciesTableProps {
  species: Species[];
  onEdit?: (s: Species) => void;
}

const origins: Record<string, string> = {
  "nativa": "Nativa",
  "exótica": "Exótica",
  "exótica invasora": "Exótica invasora",
  "ornamental": "Ornamental",
  "frutífera": "Frutífera",
  "não identificada": "Não identificada",
};

export default function SpeciesTable({ species, onEdit }: SpeciesTableProps) {
  const [search, setSearch] = useState("");
  const [filterFamily, setFilterFamily] = useState("");
  const [filterOrigin, setFilterOrigin] = useState("");

  const families = useMemo(() => {
    const set = new Set(species.map((s) => s.family));
    return Array.from(set).sort();
  }, [species]);

  const filtered = useMemo(() => {
    return species.filter((s) => {
      if (search) {
        const q = search.toLowerCase();
        if (!s.popularName.toLowerCase().includes(q) && !s.scientificName.toLowerCase().includes(q) && !s.family.toLowerCase().includes(q)) return false;
      }
      if (filterFamily && s.family !== filterFamily) return false;
      if (filterOrigin && s.origin !== filterOrigin) return false;
      return true;
    });
  }, [species, search, filterFamily, filterOrigin]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            placeholder="Buscar por nome popular, científico ou família..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none"
          />
        </div>
        <select value={filterFamily} onChange={(e) => setFilterFamily(e.target.value)} className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
          <option value="">Todas as famílias</option>
          {families.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filterOrigin} onChange={(e) => setFilterOrigin(e.target.value)} className="px-3 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-900 focus:ring-2 focus:ring-primary-200 focus:border-primary-500 outline-none">
          <option value="">Todas as origens</option>
          {Object.entries(origins).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <div className="text-xs text-foreground-500">{filtered.length} de {species.length} espécies encontradas</div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200">
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Nome popular</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Nome científico</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Família</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Origem</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Porte</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Status</th>
              <th className="text-left py-3 px-3 text-xs font-medium text-foreground-500">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                <td className="py-2.5 px-3 text-foreground-900 font-medium">{s.popularName}</td>
                <td className="py-2.5 px-3 text-foreground-500 italic">{s.scientificName}</td>
                <td className="py-2.5 px-3 text-foreground-700">{s.family}</td>
                <td className="py-2.5 px-3">
                  <span className="text-xs text-foreground-600">{origins[s.origin] || s.origin}</span>
                </td>
                <td className="py-2.5 px-3 text-foreground-600 capitalize">{s.size}</td>
                <td className="py-2.5 px-3"><SettingsStatusBadge status={s.status} /></td>
                <td className="py-2.5 px-3">
                  <button onClick={() => onEdit?.(s)} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center text-foreground-400 hover:text-primary-600 transition-colors" title="Editar">
                    <i className="ri-edit-line text-sm" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-10 text-foreground-400">
          <i className="ri-plant-line text-3xl mb-2 block" />
          <p className="text-sm">Nenhuma espécie encontrada com os filtros atuais.</p>
        </div>
      )}
    </div>
  );
}